import React, { useId, useRef, useState } from "react";
import { Button } from "@datapay-by-datacom/design-system";
import styles from "./FileDropzone.module.css";

interface FileDropzoneProps {
  /** Current file(s) - controlled component value */
  value?: File | File[] | null;
  /** Collapse to a compact uploaded-file replacement target when a value is present. */
  compactWhenSelected?: boolean;
  /** Called when files are selected/dropped */
  onChange: (files: File | File[] | null) => void;
  /** Main instruction text */
  instruction: string;
  /** Sub-instruction text (formatting, size requirements, etc.) */
  subInstruction?: string;
  /** File type filter (e.g., ".csv", ".pdf,.doc,.docx") */
  accept?: string;
  /** Whether to allow multiple file selection */
  multiple?: boolean;
  /** Whether the dropzone is disabled/processing */
  isDisabled?: boolean;
  /** Button text when not disabled */
  buttonText?: string;
  /** Button text when disabled */
  disabledButtonText?: string;
  /** Optional file filter function for additional validation */
  fileFilter?: (file: File) => boolean;
  /** Custom CSS class name */
  className?: string;
}

function selectedFiles(value: File | File[] | null | undefined): File[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export function FileDropzone({
  value,
  compactWhenSelected = false,
  onChange,
  instruction,
  subInstruction,
  accept,
  multiple = false,
  isDisabled = false,
  buttonText = "Select File",
  disabledButtonText = "Processing...",
  fileFilter,
  className,
}: FileDropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const files = compactWhenSelected ? selectedFiles(value) : [];
  const hasFile = files.length > 0;
  const showDragFeedback = compactWhenSelected && isDragOver;

  const processFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      onChange(null);
      return;
    }

    let files = Array.from(fileList);

    // Apply file filter if provided
    if (fileFilter) {
      files = files.filter(fileFilter);
    }

    if (files.length === 0) {
      onChange(null);
      return;
    }

    // Return single file or array based on multiple prop
    if (multiple) {
      onChange(files);
    } else {
      onChange(files[0]);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    if (isDisabled) return;

    processFiles(event.dataTransfer.files);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(event.target.files);
    // Clear the input value to allow selecting the same file again
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDisabled) setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setIsDragOver(false);
  };

  const handleButtonClick = () => {
    if (!isDisabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div
      className={`${styles.dropzone} ${isDisabled ? styles.disabled : ""} ${
        hasFile ? styles.hasFile : ""
      } ${showDragFeedback ? styles.dragOver : ""} ${className || ""}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div className={styles.dropzoneContent}>
        {showDragFeedback ? (
          <>
            <p className={styles.instruction}>
              {hasFile ? "Release to replace" : "Release to upload"}
            </p>
            {!hasFile && subInstruction && (
              <p
                className={`${styles.subInstruction} ${styles.dragPlaceholder}`}
                aria-hidden="true"
              >
                {subInstruction}
              </p>
            )}
          </>
        ) : hasFile ? (
          <p className={styles.uploadedRow} role="status">
            <span className={styles.uploadedLabel}>File uploaded</span>
            <span className={styles.fileName}>
              {files.map((file) => file.name).join(", ")}
            </span>
            <span className={styles.subInstruction}>Drop a file here to replace it</span>
          </p>
        ) : (
          <>
            <p className={styles.instruction}>{instruction}</p>
            {subInstruction && (
              <p className={styles.subInstruction}>{subInstruction}</p>
            )}
          </>
        )}

        <input
          ref={inputRef}
          className={styles.fileInput}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          id={inputId}
          disabled={isDisabled}
        />
        <Button
          variant="secondary"
          isDisabled={isDisabled}
          onPress={handleButtonClick}
        >
          {isDisabled ? disabledButtonText : hasFile ? "Replace file" : buttonText}
        </Button>
      </div>
    </div>
  );
}
